/**
 * Hook that sends a user turn to the Support Assistant and streams the
 * answer back into the Zustand store. Also exposes `submitFeedback` so
 * the widget can record 👍/👎 on each assistant turn.
 *
 * Authentication: this hook integrates with the existing Janus auth layer.
 * The non-streaming path uses the shared `api` axios instance (auto-attaches
 * `Authorization: Bearer <accessToken>` from localStorage + handles 401
 * refresh). The streaming path uses fetch + ReadableStream (EventSource has
 * no POST/header support) and manually attaches the same Bearer token.
 *
 * SSE wire format: the backend JSON-encodes each piece so embedded
 * "\n\n" inside the payload (notably the leading newlines of the
 * "\n\n🎬 **Tutorial em vídeo:**" suffix) doesn't collide with the SSE
 * frame terminator. Each event arrives as `data: "<json-string>"\n\n`;
 * we JSON.parse it before appending. The `[DONE]` terminator stays
 * raw and is checked literally before parse.
 *
 * Retry-reset protocol: when the backend detects that Gemini emitted the
 * literal refusal phrase but chunks WERE retrieved, it streams the
 * `SUPPORT_RETRY_RESET_MARKER` sentinel (still JSON-encoded), then
 * streams a fresh retry. The hook watches for the sentinel substring
 * in each decoded chunk, clears the in-flight assistant bubble's
 * content on receipt, and continues appending chunks — so the user
 * just sees the bubble repopulate with the correct answer.
 *
 * Feedback transport: PostHog `capture()` event with payload
 *   { session_id, request_id, feedback: 'helpful'|'not_helpful', refusal_type }
 * — already wired via AnalyticsProvider, no new BFF endpoint required for v1.
 * A dedicated backend endpoint can be added later without breaking the UI.
 */

import { useCallback } from 'react';
import api from '../utils/api';
import { useAnalytics } from './useAnalytics';
import {
  useSupportAssistantStore,
  type SupportMessage,
  type SupportFeedback,
} from '../stores/useSupportAssistantStore';

const CHAT_STREAM_URL = '/api/v1/support/chat/stream';
const CHAT_PATH = '/support/chat'; // relative to api.baseURL = '/api/v1'

// Sentinel sent by the backend stream when a false-refusal is detected
// and the retry pipeline kicks in. On receipt, the hook clears the
// in-flight assistant bubble so the retry's chunks repopulate it.
const RETRY_RESET_MARKER = '[[SUPPORT_RETRY_RESET]]';

interface NonStreamingResponse {
  answer: string;
  refusal_type?: string | null;
  citations?: string[];
  soft_warnings?: string[];
  pii_self_disclosure?: boolean;
  request_id?: string;
  latency_ms?: number;
}

function getAccessToken(): string | null {
  try {
    return localStorage.getItem('accessToken');
  } catch {
    return null;
  }
}

function parseSseChunk(chunk: string): { events: string[]; rest: string } {
  const events: string[] = [];
  let rest = chunk;
  let idx: number;
  while ((idx = rest.indexOf('\n\n')) !== -1) {
    const frame = rest.slice(0, idx);
    rest = rest.slice(idx + 2);
    const m = /^data:\s?([\s\S]*)$/.exec(frame);
    if (m) events.push(m[1]);
  }
  return { events, rest };
}

/**
 * Decode a single SSE `data:` payload. Returns null for the `[DONE]`
 * sentinel or for malformed payloads (logged + skipped instead of
 * throwing, so a single bad frame can't kill the stream).
 */
function decodePayload(ev: string): string | null {
  if (ev === '[DONE]') return null;
  // Backend wraps every chunk in JSON since v2 of the SSE format so
  // newlines inside the payload don't break frame boundaries. Strings
  // come back as JSON-encoded quoted strings; objects (none currently)
  // would deserialise to non-string and are coerced to "".
  try {
    const parsed = JSON.parse(ev);
    return typeof parsed === 'string' ? parsed : '';
  } catch {
    // Defensive fallback for any straggler frame that pre-dates the
    // JSON wire contract (e.g. a server still on the previous build
    // during a rolling deploy).
    return ev;
  }
}

async function sendStreaming(
  message: string,
  sessionId: string,
  locale: string,
  onChunk: (text: string) => void,
  onReset: () => void,
  signal: AbortSignal,
): Promise<void> {
  const token = getAccessToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
  };
  if (token) headers.Authorization = `Bearer ${token}`;

  const r = await fetch(CHAT_STREAM_URL, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify({ message, session_id: sessionId, locale }),
    signal,
  });
  if (r.status === 401) throw new Error('unauthorized');
  if (!r.ok || !r.body) throw new Error(`stream_open_failed_${r.status}`);

  const reader = r.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';

  const dispatch = (ev: string) => {
    const text = decodePayload(ev);
    if (text === null) return;
    // Split events on the retry-reset sentinel so any text before
    // and after the marker is dispatched correctly.
    if (text.includes(RETRY_RESET_MARKER)) {
      const parts = text.split(RETRY_RESET_MARKER);
      const before = parts[0];
      const after = parts.slice(1).join(RETRY_RESET_MARKER);
      if (before) onChunk(before);
      onReset();
      if (after) onChunk(after);
      return;
    }
    onChunk(text);
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const parsed = parseSseChunk(buf);
    buf = parsed.rest;
    for (const ev of parsed.events) dispatch(ev);
  }
  const tail = parseSseChunk(buf + '\n\n');
  for (const ev of tail.events) if (ev) dispatch(ev);
}

async function sendNonStreaming(
  message: string,
  sessionId: string,
  locale: string,
): Promise<NonStreamingResponse> {
  // Shared `api` instance auto-attaches Bearer token + handles 401 refresh.
  const { data } = await api.post<NonStreamingResponse>(CHAT_PATH, {
    message,
    session_id: sessionId,
    locale,
  });
  return data;
}

export interface UseSupportAssistantChatOptions {
  /** Force non-streaming fallback (default: false). */
  disableStreaming?: boolean;
  locale?: string;
}

export function useSupportAssistantChat(opts: UseSupportAssistantChatOptions = {}) {
  const {
    sessionId,
    isSending,
    messages,
    appendUserMessage,
    appendAssistantPlaceholder,
    appendAssistantChunk,
    patchAssistantMessage,
    setFeedback,
    setSending,
    setError,
  } = useSupportAssistantStore();
  const { trackEvent } = useAnalytics();

  const send = useCallback(
    async (rawMessage: string) => {
      const message = (rawMessage ?? '').trim();
      if (!message) return;
      if (isSending) return;
      setError(null);
      setSending(true);
      appendUserMessage(message);
      const assistantId = appendAssistantPlaceholder();
      const ctrl = new AbortController();
      const locale = opts.locale ?? 'pt-BR';

      try {
        if (opts.disableStreaming) {
          const resp = await sendNonStreaming(message, sessionId, locale);
          patchAssistantMessage(assistantId, {
            content: resp.answer,
            isStreaming: false,
            citations: resp.citations ?? [],
            refusalType: resp.refusal_type ?? null,
            softWarnings: resp.soft_warnings ?? [],
            requestId: resp.request_id,
          } satisfies Partial<SupportMessage>);
          return;
        }

        await sendStreaming(
          message,
          sessionId,
          locale,
          (chunk) => appendAssistantChunk(assistantId, chunk),
          () => patchAssistantMessage(assistantId, { content: '' }),
          ctrl.signal,
        );
        patchAssistantMessage(assistantId, { isStreaming: false });
      } catch (err: unknown) {
        // Stream open failure (e.g. 401, network) → non-streaming fallback,
        // which goes through axios `api` and triggers token refresh on 401.
        const errMsg = err instanceof Error ? err.message : 'unknown_error';
        try {
          const resp = await sendNonStreaming(message, sessionId, locale);
          patchAssistantMessage(assistantId, {
            content: resp.answer,
            isStreaming: false,
            citations: resp.citations ?? [],
            refusalType: resp.refusal_type ?? null,
            softWarnings: resp.soft_warnings ?? [],
            requestId: resp.request_id,
          });
        } catch {
          patchAssistantMessage(assistantId, {
            content:
              'O assistente está temporariamente indisponível. Tente novamente em instantes.',
            isStreaming: false,
            refusalType: 'service_unavailable',
          });
          setError(errMsg);
        }
      } finally {
        setSending(false);
      }
    },
    [
      opts.locale,
      opts.disableStreaming,
      sessionId,
      isSending,
      appendUserMessage,
      appendAssistantPlaceholder,
      appendAssistantChunk,
      patchAssistantMessage,
      setSending,
      setError,
    ],
  );

  const submitFeedback = useCallback(
    (assistantMessageId: string, value: SupportFeedback) => {
      // Optimistically persist on the message
      setFeedback(assistantMessageId, value);

      // Find the message for additional context in the analytics event
      const msg = messages.find((m) => m.id === assistantMessageId);
      try {
        trackEvent('support_assistant_feedback', {
          session_id: sessionId,
          message_id: assistantMessageId,
          request_id: msg?.requestId ?? null,
          feedback: value,
          refusal_type: msg?.refusalType ?? null,
          had_citations: !!(msg?.citations?.length),
        });
      } catch {
        /* analytics best-effort */
      }
    },
    [messages, sessionId, setFeedback, trackEvent],
  );

  return { send, submitFeedback, isSending };
}
